import { Slide, useMediaQuery } from '@mui/material';
import { Button } from 'flowbite-react';
import React, { useEffect } from 'react';
import Draggable from 'react-draggable';
import { HiMiniArrowLeft } from 'react-icons/hi2';
import { IoClose } from 'react-icons/io5';
import { MdDragIndicator } from 'react-icons/md';
import { useParams, useSearchParams } from 'react-router-dom';
import { Drawer } from '../../shared/Drawer';
import { TypeOfUsers } from 'types';
import NewRecruiterCandidatesBulkActionsOnConncetedUsers from './NewRecruiterCandidatesBulkActionsOnConncetedUsers';
import AssessmentUsersBulkAction from './AssessmentUsersBulkAction';
import CampusUserBulkAction from './CampusUserBulkAction';
import DriveUsersBulkAction from './DriveUsersBulkAction';
import DrivePositionsBulkAction from './DrivePositionsBulkAction';

const BulkActionComponent = ({
	onChange,
	typeOfUsers,
	allIds,
	bulkSelectedIds,
	setBulkSelectedIds,
}: {
	allIds: string[];
	bulkSelectedIds: string[];
	typeOfUsers: TypeOfUsers | string;
	onChange?: (candidates: string[]) => void;
	setBulkSelectedIds: React.Dispatch<React.SetStateAction<string[]>>;
}) => {
	const { positionId } = useParams();
	const isMobile = useMediaQuery('(max-width:960px)');
	const [searchParams, setSearchParams] = useSearchParams();
	const tab = searchParams.get('tab');
	const bulkSelect = searchParams.get('bulkSelect') === 'true';
	const [dragablePosition, setDragablePosition] = React.useState({ x: 0, y: 0 });
	const [defaultBulkAction, setDefaultBulkAction] = React.useState<string | null>();
	const [dummyState, setDummyState] = React.useState(false); // Dummy state to force re-render

	const resetPosition = () => {
		setDragablePosition({ x: 0, y: 0 });
	};
	const handleCloseBulk = () => {
		searchParams.delete('bulkSelect');
		setSearchParams(searchParams);
		setBulkSelectedIds([]);
	};

	useEffect(() => {
		if (isMobile) {
			return;
		}
		if (defaultBulkAction) {
			return;
		}
		const handleSearchParamsChange = () => {
			setDummyState(prev => !prev); // Toggle dummy state to force re-render
		};

		// Listen for changes in the searchParams
		const observer = new MutationObserver(handleSearchParamsChange);
		observer.observe(document, {
			subtree: true,
			childList: true,
			attributes: true,
		});

		return () => observer.disconnect();
	}, [searchParams, defaultBulkAction]);

	useEffect(() => {
		onChange && onChange(bulkSelectedIds);
	}, [bulkSelectedIds]);

	useEffect(() => {
		const handleEsc = (event: KeyboardEvent) => {
			if (event.key === 'Escape') {
				handleCloseBulk();
			}
		};
		document.addEventListener('keydown', handleEsc);
		return () => document.removeEventListener('keydown', handleEsc);
	}, [searchParams, setSearchParams]);

	useEffect(() => {
		onChange && onChange(bulkSelectedIds);
	}, [bulkSelectedIds]);

	const BulkActionContent = (typeOfUsers: TypeOfUsers | string) => {
		switch (typeOfUsers) {
			case TypeOfUsers.FRESHER: {
				return {
					actions: (
						<div className="flex items-center gap-2 flex-wrap justify-center">
							<Button
								size="sm"
								color="light"
								disabled={!bulkSelectedIds.length}
								className="focus:ring-[1px] focus:ring-gray-400"
								onClick={() => {
									setDefaultBulkAction('share-profile');
								}}
							>
								Share profiles
							</Button>
							<Button
								size="sm"
								color="light"
								disabled={!bulkSelectedIds.length}
								className="focus:ring-[1px] focus:ring-gray-400"
								onClick={() => {
									setDefaultBulkAction('message');
								}}
							>
								Send message
							</Button>
							<Button
								size="sm"
								color="light"
								disabled={!bulkSelectedIds.length}
								className="focus:ring-[1px] focus:ring-gray-400"
								onClick={() => {
									setDefaultBulkAction('send-assignment');
								}}
							>
								Send assignment
							</Button>
							<Button
								size="sm"
								color="light"
								disabled={!bulkSelectedIds.length}
								className="focus:ring-[1px] focus:ring-gray-400"
								onClick={() => {
									setDefaultBulkAction('send-online-test');
								}}
							>
								Send online test
							</Button>
							<Button
								size="sm"
								color="light"
								disabled={!bulkSelectedIds.length}
								className="focus:ring-[1px] focus:ring-gray-400"
								onClick={() => {
									setDefaultBulkAction('move-to-next');
								}}
							>
								Move to
							</Button>
							<Button
								size="sm"
								color="light"
								disabled={!bulkSelectedIds.length}
								className="focus:ring-[1px] focus:ring-gray-400"
								onClick={() => {
									setDefaultBulkAction('bulk-reject');
								}}
							>
								Reject Candidates
							</Button>
							<Button
								size="sm"
								color="light"
								disabled={!bulkSelectedIds.length}
								className="focus:ring-[1px] focus:ring-gray-400"
								onClick={() => {
									setDefaultBulkAction('bulk-undo-auto-reject');
								}}
							>
								Undo Auto-Reject
							</Button>
						</div>
					),
					content: (
						<NewRecruiterCandidatesBulkActionsOnConncetedUsers
							selectedTab={tab || ''}
							bulkSelectedCandidates={bulkSelectedIds}
							positionId={positionId}
							defaultBulkAction={defaultBulkAction}
							onFinish={() => {
								setBulkSelectedIds([]);
								handleCloseBulk();
								resetPosition();
								setDefaultBulkAction(null);
							}}
						/>
					),
				};
			}
			case TypeOfUsers.CAMPUS: {
				return {
					actions: (
						<div className="flex items-center gap-2 flex-wrap justify-center">
							<Button
								size="sm"
								color="light"
								disabled={!bulkSelectedIds.length}
								className="focus:ring-[1px] focus:ring-gray-400"
								onClick={() => {
									setDefaultBulkAction('invite-for-placement-drive');
								}}
							>
								Invite For Placement Drive
							</Button>
						</div>
					),
					content: (
						<CampusUserBulkAction
							defaultBulkAction={defaultBulkAction}
							bulkSelectedCandidates={bulkSelectedIds}
							onFinish={() => {
								setBulkSelectedIds([]);
								handleCloseBulk();
								resetPosition();
								setDefaultBulkAction(null);
							}}
						/>
					),
				};
			}
			case TypeOfUsers.ASSESSMENT: {
				return {
					actions: <AssessmentUsersBulkAction bulkSelectedCandidates={bulkSelectedIds} showModalUi={false} />,
					content: <AssessmentUsersBulkAction bulkSelectedCandidates={bulkSelectedIds} showModalUi={true} />,
				};
			}
			case TypeOfUsers.DRIVE: {
				return {
					actions: (
						<DriveUsersBulkAction
							showModalUi={false}
							onActionClick={(action: string) => {
								setDefaultBulkAction(action);
							}}
							bulkSelectedCandidates={bulkSelectedIds}
							onFinish={() => {
								setBulkSelectedIds([]);
								setDefaultBulkAction(null);
							}}
						/>
					),
					content: (
						<DriveUsersBulkAction
							showModalUi={true}
							bulkSelectedCandidates={bulkSelectedIds}
							onActionClick={(action: string) => {
								setDefaultBulkAction(action);
							}}
							defaultBulkAction={defaultBulkAction || ''}
							onFinish={() => {
								setBulkSelectedIds([]);
								handleCloseBulk();
								resetPosition();
								setDefaultBulkAction(null);
							}}
						/>
					),
				};
			}
			case 'offline-drive-positions': {
				return {
					actions: (
						<DrivePositionsBulkAction
							showModalUi={false}
							defaultBulkAction={defaultBulkAction || ''}
							bulkSelectedCompanies={bulkSelectedIds}
						/>
					),
					content: (
						<DrivePositionsBulkAction
							showModalUi={true}
							defaultBulkAction={defaultBulkAction || ''}
							bulkSelectedCompanies={bulkSelectedIds}
						/>
					),
				};
			}
		}
	};

	return (
		<div>
			{/* Bulk select nav mobile */}
			{isMobile ? (
				<Slide in={bulkSelect} direction="down" unmountOnExit mountOnEnter>
					<div className="fixed top-0 inset-x-0 z-[50] h-[48px] bg-[#28293D] w-full text-white flex items-center justify-between px-4 py-3">
						<button
							onClick={() => {
								handleCloseBulk();
							}}
						>
							<HiMiniArrowLeft className="h-[24px] w-[24px]" />
						</button>

						<button
							className="text-white"
							onClick={() => {
								bulkSelectedIds?.length == allIds?.length ? setBulkSelectedIds([]) : setBulkSelectedIds(allIds);
							}}
						>
							{bulkSelectedIds?.length == allIds?.length ? 'Clear all' : 'Select all'}
						</button>
					</div>
				</Slide>
			) : null}

			{isMobile ? null : (
				<Draggable
					handle="div"
					onDrag={(e, ui) => {
						const { x, y } = ui;
						setDragablePosition({ x, y });
					}}
					position={dragablePosition}
				>
					<Slide
						in={bulkSelect}
						direction="up"
						mountOnEnter
						unmountOnExit
						className="fixed z-[10] bottom-[10%] inset-x-[0] w-fit mx-auto ml-auto whitespace-pre flex items-center gap-2 rounded-full py-[12px] px-[16px] bg-[#28293D] dark:bg-[#374151]"
					>
						<div>
							<strong className="text-[#C7C9D9] cursor-move">
								<MdDragIndicator className="w-[24px] h-[24px]" />
							</strong>

							<div className="text-[#E4E4EB] flex items-center gap-1.5 mr-1">
								<span className="bg-[#7777E7] flex items-center justify-center p-1 text-center leading-[normal] w-full h-full max-w-fit max-h-[24px] min-w-[24px] min-h-[24px] rounded-full">
									{bulkSelectedIds.length}
								</span>
								candidates selected
							</div>

							<div className="mr-1">
								<button
									className="text-[#9DBFF9] underline"
									onClick={() => {
										bulkSelectedIds?.length == allIds?.length
											? setBulkSelectedIds([])
											: setBulkSelectedIds(allIds);
									}}
								>
									{bulkSelectedIds?.length == allIds?.length ? 'Clear all' : 'Select all'}
								</button>
							</div>

							{BulkActionContent(typeOfUsers)?.actions}

							<button className="text-[#C7C9D9]" onClick={handleCloseBulk}>
								<IoClose className="w-[24px] h-[24px]" />
							</button>
						</div>
					</Slide>
				</Draggable>
			)}

			{/* Bulk Select Drawer */}
			<Drawer
				hideBackdrop={isMobile}
				minHeight="max-content"
				isModal={!isMobile}
				disableScrollLock={isMobile}
				boxShadow="0px -8px 32px 0px #00000040"
				isOpen={isMobile ? bulkSelectedIds?.length > 0 : !!defaultBulkAction}
				onClose={() => {
					if (isMobile) {
						handleCloseBulk();
						resetPosition();
					}
					setDefaultBulkAction(null);
				}}
				header={
					<h1 className="text-[#1C1C28] dark:text-inherit text-lg font-inter font-[500] px-5 pt-4">
						Selected candidates ({bulkSelectedIds?.length})
					</h1>
				}
				body={<>{BulkActionContent(typeOfUsers)?.content}</>}
			/>
		</div>
	);
};

export default BulkActionComponent;
